<div
  class="flex flex-col gap-2.5 text-xs"
  [class]="
    framed() ? 'bg-elevated border-default rounded border px-3.5 pt-2.5 pb-3.5' : ''
  ">
  @if (findings().length > 0) {
    <!-- Unframed, the row bleeds over the host's gutters so rows scrolling under it stay hidden. -->
    <div
      class="bg-elevated sticky top-0 z-10 flex flex-wrap items-center gap-2 py-px"
      [class]="framed() ? '' : '-mx-3.5 px-3.5 pb-2'">
      <label
        class="border-default text-text-tertiary focus-within:border-accent-focus flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-sm border pr-1 pl-2">
        <i [class]="ICONS.magnifyingGlass" aria-hidden="true"></i>
        <input
          #search
          data-testid="findings-search"
          type="search"
          class="text-text-primary placeholder:text-text-tertiary min-w-0 flex-1 bg-transparent text-xs outline-none [&::-webkit-search-cancel-button]:appearance-none"
          [value]="query()"
          [placeholder]="'features.plan.findings.search' | translate"
          [attr.aria-label]="'features.plan.findings.search' | translate"
          (input)="onSearch($event)" />
        @if (query()) {
          <button
            type="button"
            data-testid="findings-search-clear"
            class="text-text-tertiary hover:text-text-primary flex h-5.5 w-5.5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-sm transition-colors"
            [attr.aria-label]="'common.clear' | translate"
            [title]="'common.clear' | translate"
            (click)="clearSearch(search)">
            <i [class]="ICONS.x" aria-hidden="true"></i>
          </button>
        }
      </label>
      <ng-content select="[findingsScope]" />
    </div>
  }

  <!-- Outdated strip. Suppressed while busy: an unchecked or stale plan auto-checks, and
       offering "Check now" against a run already in flight would start a second one. -->
  @if (!loading() && findingsAreStale()) {
    <div class="bg-info-soft flex flex-wrap items-center gap-2 rounded-sm py-1 pr-1 pl-2">
      <span class="bg-info h-2 w-2 shrink-0 rounded-full" aria-hidden="true"></span>
      <span class="text-text-secondary flex-1 text-[11px]">
        {{ 'features.plan.findings.stale_title' | translate }}
      </span>
      <app-button
        [label]="'features.plan.findings.check_now' | translate"
        [icon]="ICONS.arrowsClockwise"
        [type]="'cancel'"
        (clickEvent)="checkRequested.emit()" />
    </div>
  }

  @if (!loading() && checkFailed()) {
    <div class="bg-danger-soft flex flex-wrap items-center gap-2 rounded-sm py-1 pr-1 pl-2">
      <span class="bg-danger h-2 w-2 shrink-0 rounded-full" aria-hidden="true"></span>
      <span class="text-text-secondary flex-1 text-[11px]">
        {{ 'features.plan.findings.check_failed' | translate }}
      </span>
      <app-button
        [label]="'features.plan.findings.check_now' | translate"
        [icon]="ICONS.arrowsClockwise"
        [type]="'cancel'"
        (clickEvent)="checkRequested.emit()" />
    </div>
  }

  <!-- The whole line toggles the list so the target is the banner, not its caret. The caret
       stays the keyboard control: its click bubbles here, so it carries no handler of its own. -->
  <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events -->
  <div
    data-testid="findings-status-line"
    class="-mx-1.5 flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1 rounded-sm px-1.5 transition-colors"
    [class]="foldable() ? 'hover:bg-interactive cursor-pointer' : ''"
    (click)="foldable() && toggleExpanded()">
    <span
      class="flex h-5 w-5 shrink-0 items-center justify-center rounded"
      [class]="mark().tone"
      aria-hidden="true">
      <i [class]="mark().icon" class="text-xs"></i>
    </span>
    @if (live()) {
      <span
        data-testid="findings-live"
        class="bg-warning-soft text-warning-ink rounded-full px-2 py-px text-[11px] font-semibold">
        {{ 'features.plan.findings.live_label' | translate }}
      </span>
    }
    @if (loading()) {
      <!-- Every label below states a status, and none of them is known yet: an empty
           `findings` while the read is in flight would otherwise read as "No issues found". -->
      <span
        data-testid="findings-loading"
        class="text-text-secondary text-[13px] font-semibold">
        {{ 'common.loading' | translate }}
      </span>
    } @else if (findings().length === 0) {
      @if (unavailable()) {
        <span
          data-testid="findings-unavailable"
          class="text-text-tertiary text-[13px] font-semibold">
          {{ 'features.plan.findings.unavailable' | translate }}
        </span>
      } @else if (planNeverChecked()) {
        <span class="text-text-tertiary text-[13px] font-semibold">
          {{ 'features.plan.findings.never_checked' | translate }}
        </span>
      } @else {
        <span class="text-text-secondary text-[13px] font-semibold">
          {{ 'features.plan.findings.checked_clean' | translate }}
        </span>
      }
    } @else {
      @let counts = status();
      @if (counts.showBlocking) {
        <span class="text-danger text-[13px] font-semibold">
          {{
            (counts.blocking.count < counts.blocking.total
              ? 'features.plan.findings.blocking_label_narrowed'
              : 'features.plan.findings.blocking_label'
            ) | translate: counts.blocking
          }}
        </span>
      }
      @if (counts.showWarnings) {
        <span
          class="text-[13px] font-semibold"
          [class]="counts.showBlocking ? 'text-text-secondary' : 'text-warning-ink'">
          {{
            (counts.warnings.count < counts.warnings.total
              ? 'features.plan.findings.warning_label_narrowed'
              : 'features.plan.findings.warning_label'
            ) | translate: counts.warnings
          }}
        </span>
      }
    }

    <span class="flex-1"></span>

    @if (!loading()) {
      @if (filtersActive()) {
        <button
          type="button"
          data-testid="findings-clear-filters"
          class="text-accent-main hover:bg-accent-interactive cursor-pointer rounded-sm px-1 py-0.5 text-[11px] transition-colors"
          (click)="clearFilters(); $event.stopPropagation()">
          {{ 'features.plan.findings.clear_filters' | translate }}
        </button>
      }
      @if (findings().length === 0) {
        <!-- The stale strip above already offers this action while stale. Live findings
             are the session's, so a check of the saved plan would change nothing shown. -->
        @if (!findingsAreStale() && !live() && !unavailable()) {
          <app-button
            data-testid="findings-check-now"
            [label]="'features.plan.findings.check_now' | translate"
            [icon]="ICONS.arrowsClockwise"
            [type]="'cancel'"
            (clickEvent)="checkRequested.emit()" />
        }
      } @else {
        <button
          type="button"
          data-testid="findings-fold-all"
          class="text-text-secondary hover:text-accent-main flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-sm text-sm transition-colors"
          [attr.aria-expanded]="expanded()"
          [attr.aria-label]="
            (expanded() ? 'features.plan.findings.fold' : 'features.plan.findings.unfold')
              | translate
          "
          [title]="
            (expanded() ? 'features.plan.findings.fold' : 'features.plan.findings.unfold')
              | translate
          ">
          <i [class]="expanded() ? ICONS.caretUp : ICONS.caretDown" aria-hidden="true"></i>
        </button>
      }
    }
  </div>

  @if (expanded()) {
    @if (filtered().available.length > 0) {
      <div class="border-weak flex flex-col gap-2 border-b pb-1.5">
        <button
          type="button"
          data-testid="findings-categories-toggle"
          class="text-text-secondary hover:text-text-primary flex w-full cursor-pointer items-center gap-1.5 py-0.5 text-left text-[11px] font-semibold transition-colors"
          [attr.aria-expanded]="pillsOpen()"
          (click)="togglePills()">
          <i
            [class]="pillsOpen() ? ICONS.caretDown : ICONS.caretRight"
            class="text-text-tertiary text-[10px]"
            aria-hidden="true"></i>
          <span>{{ 'features.plan.findings.categories' | translate }}</span>
          @if (pickedCount() > 0) {
            <span class="text-accent-main font-medium">
              {{
                'features.plan.findings.categories_picked'
                  | translate: { count: pickedCount(), total: filtered().available.length }
              }}
            </span>
          }
        </button>
        @if (pillsOpen()) {
          <div
            class="flex flex-wrap gap-1.5"
            role="group"
            [attr.aria-label]="'features.plan.findings.categories' | translate">
            @for (group of filtered().available; track group.code) {
              @let isPicked = picked().has(group.code);
              <button
                type="button"
                data-testid="findings-pill"
                class="inline-flex h-5.5 cursor-pointer items-center gap-1.5 rounded-full border px-2 text-[11px] transition-colors"
                [class]="
                  isPicked
                    ? 'border-accent-main bg-accent-interactive text-accent-main'
                    : 'border-default bg-elevated text-text-secondary hover:border-strong'
                "
                [attr.aria-pressed]="isPicked"
                (click)="togglePick(group.code)">
                <span>{{ group.label }}</span>
                <span
                  class="font-mono font-semibold tabular-nums"
                  [class]="isPicked ? 'text-accent-main' : 'text-text-primary'"
                  >{{ group.occurrences.length }}</span
                >
              </button>
            }
          </div>
        }
      </div>
    }

    @if (filtered().groups.length > 0) {
      <div
        class="flex flex-col gap-1.5"
        [class.max-h-80]="listScrolls()"
        [class.overflow-y-auto]="listScrolls()">
        @for (group of filtered().groups; track group.code) {
          @let open = unfolded().has(group.code);
          <section>
            <div
              class="group hover:bg-interactive -mx-1.5 flex items-start rounded-t-sm border-b transition-colors"
              [class]="open ? 'border-default' : 'border-weak'">
              <button
                type="button"
                data-testid="finding-group-toggle"
                class="grid min-w-0 flex-1 cursor-pointer grid-cols-[14px_minmax(0,1fr)_auto] items-baseline gap-x-2 gap-y-0.5 py-1.5 pl-1.5 text-left"
                [attr.aria-expanded]="open"
                (click)="toggleGroup(group.code)">
                <i
                  [class]="open ? ICONS.caretDown : ICONS.caretRight"
                  class="text-text-tertiary self-center text-[11px]"
                  aria-hidden="true"></i>
                <span #labelRef class="text-text-primary truncate text-xs font-semibold">{{
                  group.label
                }}</span>
                <span class="inline-flex items-center gap-2">
                  <span
                    class="bg-weak h-0.75 w-12 self-center overflow-hidden rounded-sm"
                    aria-hidden="true">
                    <span
                      data-testid="finding-group-share"
                      class="block h-full min-w-0.5"
                      [class]="group.blocksOptimization ? 'bg-danger' : 'bg-warning'"
                      [style.width.%]="share(group)"></span>
                  </span>
                  <span
                    class="text-text-primary font-mono text-xs font-semibold tabular-nums"
                    >{{ group.occurrences.length }}</span
                  >
                </span>
                <span
                  class="text-text-tertiary col-start-2 col-end-4 truncate text-[10.5px]">
                  <span data-testid="finding-group-code" class="font-mono">{{
                    group.code
                  }}</span>
                  @if (worstSize(group); as size) {
                    <span class="ml-2">
                      {{
                        'features.plan.findings.worst'
                          | translate
                            : {
                                value: compactSeconds(size.seconds),
                                word:
                                  ('features.plan.findings.size.' + size.word | translate)
                              }
                      }}
                    </span>
                  }
                </span>
              </button>
              <span class="inline-flex shrink-0 items-center gap-1 py-1.5 pr-1.5 pl-1">
                @if (group.blocksOptimization) {
                  <span
                    class="bg-danger rounded-sm px-1 py-px text-[9.5px] font-bold tracking-wide text-white uppercase">
                    {{ 'features.plan.findings.blocks_badge' | translate }}
                  </span>
                }
                <span #headingCopyBtnRef class="inline-flex">
                  <button
                    type="button"
                    data-testid="heading-copy"
                    class="text-text-tertiary hover:text-text-primary flex h-5 w-5 cursor-pointer items-center justify-center rounded opacity-0 transition-colors group-hover:opacity-100 focus-visible:opacity-100"
                    [attr.aria-label]="
                      'features.plan.findings.copy_heading_tooltip' | translate
                    "
                    (click)="copyFindingHeading(group)">
                    <i [class]="ICONS.copy" class="text-xs" aria-hidden="true"></i>
                  </button>
                  <app-tooltip
                    [target]="headingCopyBtnRef"
                    [content]="'features.plan.findings.copy_heading_tooltip' | translate"
                    [position]="'top'"
                    [maxWidth]="220" />
                </span>
              </span>
            </div>
            <app-tooltip [target]="labelRef" [content]="group.label" [position]="'top'" />

            @if (open) {
              @let rows =
                isRevealed(group.code)
                  ? group.occurrences
                  : group.occurrences.slice(0, firstRows);
              <ul class="m-0 mb-2 list-none p-0">
                @for (occurrence of rows; track occurrence.key) {
                  <li
                    class="group hover:bg-interactive border-weak -mx-1.5 flex items-start gap-2 rounded-sm border-b transition-colors last:border-b-0">
                    <button
                      type="button"
                      data-testid="occurrence-row"
                      class="min-w-0 flex-1 cursor-pointer py-1.5 pl-1.5 text-left text-xs"
                      aria-haspopup="dialog"
                      (click)="openFindingCard(group, occurrence, $event)">
                      <app-finding-message
                        class="text-text-secondary"
                        [parts]="occurrence.parts"
                        [body]="occurrence.body"
                        [sameDay]="sameDay()"
                        [typeLabels]="entityTypeLabels()" />
                    </button>
                    <span #copyBtnRef class="inline-flex shrink-0 py-1.5 pr-1.5">
                      <button
                        type="button"
                        class="text-text-tertiary hover:text-text-primary flex h-5 w-5 cursor-pointer items-center justify-center rounded opacity-0 transition-colors group-hover:opacity-100 focus-visible:opacity-100"
                        [attr.aria-label]="'features.plan.check.copy_tooltip' | translate"
                        (click)="copyFindingMessage(occurrence, $event)">
                        <i [class]="ICONS.copy" class="text-xs" aria-hidden="true"></i>
                      </button>
                      <app-tooltip
                        [target]="copyBtnRef"
                        [content]="'features.plan.check.copy_tooltip' | translate"
                        [position]="'top'"
                        [maxWidth]="220" />
                    </span>
                  </li>
                }
                @if (rows.length < group.occurrences.length) {
                  <li class="pt-1">
                    <button
                      type="button"
                      data-testid="finding-group-more"
                      class="text-accent-main hover:bg-accent-interactive -ml-1 cursor-pointer rounded-sm px-1 py-0.5 text-[11px] transition-colors"
                      (click)="reveal(group.code)">
                      {{
                        'features.plan.findings.show_more'
                          | translate: { count: group.occurrences.length - rows.length }
                      }}
                    </button>
                  </li>
                }
              </ul>
            }
          </section>
        }
      </div>
    }
    @if (filtered().searchMatchedNothing) {
      <p
        data-testid="findings-no-match"
        class="text-text-tertiary m-0 py-1.5 text-[11.5px]">
        {{ 'features.plan.findings.no_match' | translate }}
      </p>
    }
    @if (hiddenNoteKey(); as noteKey) {
      @if (filtered().hiddenByScope > 0) {
        <p
          data-testid="findings-hidden-note"
          class="border-weak text-text-tertiary m-0 border-t pt-2 text-[11px]">
          {{ noteKey | translate: { count: filtered().hiddenByScope } }}
        </p>
      }
    }
  }
</div>
